import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, FluentProvider, webLightTheme } from '@fluentui/react-components';
import React from 'react';
import { GET_COMPANY_CREATION_STATUSES } from '../../utils/statApi/CompanyCreationApi';
import { ICompanyCreationRequestListPageState } from './CompanyCreationListPage';
import AcquisitionFilter from './AcquisitionFilter';
import { CompanyCreationStatus } from './types';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import IDropdownOption from '../../utils/types/IDropdownOption';

const dropDownStyles = { width: '200px' };

interface IListFilterProps {
  requestorOptions: IDropdownOption[];
  acquisitionOptions: IDropdownOption[];
  filterState: ICompanyCreationRequestListPageState;
  setFilterState: React.Dispatch<React.SetStateAction<ICompanyCreationRequestListPageState>>;
}

const CompanyCreationListFilter = ({
  requestorOptions,
  acquisitionOptions,
  filterState,
  setFilterState,
}: IListFilterProps): JSX.Element => {
  const { loading, data } = useQuery(GET_COMPANY_CREATION_STATUSES);
  const { companyCreationStatuses } = !loading && data ? data : { companyCreationStatuses: [] };

  const userFriendlyStatusNames: IDropdownOption[] =
    companyCreationStatuses?.map((ccs: IDropdownOption) => ({
      key: ccs.key.toString(),
      text: ccs.text.replace(/([A-Z])/g, ' $1').trim(), // Add spaces between words
    })) || [];

  const onReviewStatusChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    setFilterState({ ...filterState, statusIds: itemIds });
  };

  const onAssignmentFilterChange = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    setFilterState({ ...filterState, requestorIds: itemIds });
  };

  const onResetFiltersClick = (): void => {
    setFilterState({
      statusIds: [
        CompanyCreationStatus.RequestStarted,
        CompanyCreationStatus.CompanyCodeRequested,
        CompanyCreationStatus.PendingSubledgerInfo,
        CompanyCreationStatus.PendingStatToolSetup,
      ],
      requestorIds: [],
      acquisitionIds: [],
    });
  };

  return (
    <Stack>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <h2>Filter</h2>
        <FluentProvider theme={webLightTheme}>
          <Button onClick={onResetFiltersClick}>Reset Filters</Button>
        </FluentProvider>
      </Stack>
      <Stack tokens={{ childrenGap: 10 }} wrap>
        <MultiSelectDropdown
          label="Requestor"
          onChange={onAssignmentFilterChange}
          selectedKeys={filterState?.requestorIds?.map((id) => id.toString())}
          placeholder="No Filter Selected"
          options={requestorOptions}
          styles={dropDownStyles}
        />
        <MultiSelectDropdown
          label="Status"
          onChange={onReviewStatusChange}
          selectedKeys={filterState?.statusIds?.map((id) => id.toString())}
          placeholder="No Filter Selected"
          options={userFriendlyStatusNames}
          styles={dropDownStyles}
        />
        <AcquisitionFilter
          filterState={filterState}
          setFilterState={setFilterState}
          acquisitionOptions={acquisitionOptions}
        />
      </Stack>
    </Stack>
  );
};

export default CompanyCreationListFilter;
